import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams } from "@tanstack/react-router"
import { LayoutGroup } from "motion/react"
import {
	IconShareUpRightOutline18 as ExternalLink,
	IconMagnifierOutline18,
	IconXmarkOutline18,
} from "nucleo-ui-outline-18"

import type { Changelog, ChangelogTranslation } from "@productlane/zero"
import { cn } from "@productlane/lib/src/browser"
import { getPortalUrl } from "@productlane/lib/src/domain"
import { getChangelogSlug } from "@productlane/lib/src/portal-slugs"
import {
	Button,
	Empty,
	IconButton,
	Input,
	NewRequestIcon,
	Tooltip,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { Editor } from "@tiptap/react"
import EmptyChangelogImage from "@/assets/empty-states/empty-changelog.png"
import { DocViewers } from "@/components/doc-viewers"
import { MetaInfo } from "@/components/meta-info"
import { PortalInstanceSwitcher } from "@/components/portal-instance-switcher"
import { RoleCheck } from "@/components/shared/role-check"
import { TranslateWithAIButton } from "@/components/translate-with-ai-button"
import { TranslationOverlay } from "@/components/translation-overlay"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { generateId } from "@/lib/id"
import { useIsTranslationRunning } from "@/lib/translation-status"
import { buildTranslationTargets } from "@/lib/translation-targets"
import { useJoinDoc } from "@/lib/use-doc-presence"
import {
	getEmptyChangelogLanguages,
	isChangelogTranslationEmpty,
	useChangelogTranslations,
} from "@/lib/use-item-translations"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { LanguageSelector } from "@/screens/settings/settings-pages/language-selector"
import { HowItWorksButton } from "@/screens/settings/settings-switches/how-it-works-button"
import {
	getActivePortalInstanceId,
	useActivePortalInstance,
} from "@/stores/active-portal-instance-store"
import { MobileBackButton } from "../mobile-back-button"
import { BreadcrumbSlash, PageHeader } from "../page-header"
import { PublishPortalPageButton } from "../portal/publish-portal-page-button"
import { CopilotOverlay } from "../threads/components/right-bar/ai-chat/copilot-overlay"
import { CopilotToggleButton } from "../threads/components/right-bar/ai-chat/copilot-toggle-button"
import { ChangelogActions, ChangelogOverflowMenu } from "./changelog-actions"
import { getChangelogBreadcrumbTitle } from "./changelog-breadcrumb"
import {
	ChangelogProvider,
	useChangelogContext,
	useChangelogLanguage,
} from "./changelog-context"
import { coverBlurhashFor } from "./changelog-cover"
import { ChangelogEntry } from "./changelog-entry"
import { sortChangelogsForNav } from "./changelog-order"
import { ChangelogOverview } from "./changelog-overview"
import { isOnPortal } from "./changelog-portals"
import { ChangelogPreview } from "./changelog-preview"
import { ChangelogSearchResults } from "./changelog-search-results"
import { ChangelogSettings } from "./changelog-settings"
import { RestorePreviewBanner } from "./restore-preview-banner"

const changelogQueries = () => ({
	changelogs: queries.changelogs(),
})

export const changelogPagePreloadQueries = () => [changelogQueries().changelogs]

export function ChangelogScreen() {
	const zero = useZero()

	const params = useParams({ strict: false })
	const changelogId = params.id ?? ""
	const navigate = useNavigate()

	const workspace = useZeroWorkspace()
	const [changelogs] = useQuery(changelogQueries().changelogs)

	const handleCreateNewChangelog = async () => {
		const id = generateId()
		await mutateOk(
			zero.mutate(
				mutators.changelogs.create({
					id,
					dateIso: new Date().toISOString(),
					portalInstanceId: getActivePortalInstanceId(),
				}),
			),
		)
		analytics.track("create-changelog", { id })
		void navigate({ to: "/edit-changelog/$id", params: { id } })
	}

	const emptyChangelogImage = useMemo(() => {
		return EmptyChangelogImage
	}, [])

	// Add ESC key shortcut to navigate to empty state
	useShortcut(
		"Escape",
		() => {
			if (changelogId) {
				void navigate({ to: "/edit-changelog", replace: true })
			}
		},
		{
			enabled: !!changelogId,
		},
	)

	if (!workspace) {
		return null
	}

	return (
		<ChangelogProvider>
			<ChangelogScreenContent
				changelogId={changelogId}
				changelogs={changelogs ?? []}
				workspace={workspace}
				handleCreateNewChangelog={handleCreateNewChangelog}
				emptyChangelogImage={emptyChangelogImage}
			/>
		</ChangelogProvider>
	)
}

function ChangelogScreenContent({
	changelogId,
	changelogs,
	workspace,
	handleCreateNewChangelog,
	emptyChangelogImage,
}: {
	changelogId: string
	changelogs: readonly (Changelog & {
		translations?: readonly ChangelogTranslation[]
	})[]
	workspace: NonNullable<ReturnType<typeof useZeroWorkspace>>
	handleCreateNewChangelog: () => Promise<void>
	emptyChangelogImage: string
}) {
	const { previewVersion, setPreviewVersion } = useChangelogContext()
	const { selectedLanguage, setSelectedLanguage } = useChangelogLanguage()
	const navigate = useNavigate()

	const portalInstances = workspace.portalInstances ?? []
	// Multi-portal active as soon as 1+ portal beyond the implicit Root exists.
	const multiPortalActive = portalInstances.length >= 1
	const [activePortalInstanceId, setActivePortalInstanceId] =
		useActivePortalInstance(portalInstances)
	const activePortalSlug =
		portalInstances.find((instance) => instance.id === activePortalInstanceId)
			?.slug ?? null

	// Switching portal while a specific changelog is open would otherwise leave
	// the URL pointing at an id that isn't in the new portal's filtered list,
	// rendering a blank panel. Nudge back to the list root so the user lands on
	// the new portal's changelogs.
	const handlePortalChange = useCallback(
		(id: string | null) => {
			setActivePortalInstanceId(id)
			if (changelogId) void navigate({ to: "/edit-changelog" })
		},
		[setActivePortalInstanceId, changelogId, navigate],
	)

	// An entry can sit on several portals, so the list keeps the ones the active
	// portal is among.
	const filteredChangelogs = useMemo(() => {
		if (!multiPortalActive) return changelogs
		return changelogs.filter((c) => isOnPortal(c, activePortalInstanceId))
	}, [multiPortalActive, changelogs, activePortalInstanceId])

	const [query, setQuery] = useState("")
	const [isSearchOpen, setIsSearchOpen] = useState(false)
	const isSearching = isSearchOpen && query.trim().length > 0
	const closeSearch = useCallback(() => {
		setQuery("")
		setIsSearchOpen(false)
	}, [])

	// j/k and the arrow keys step through posts in the overview's order while
	// one is open; stepping up past the first goes back to the overview.
	const orderedIds = useMemo(
		() => sortChangelogsForNav(filteredChangelogs).map((c) => c.id),
		[filteredChangelogs],
	)
	const stepTo = useCallback(
		(offset: 1 | -1) => {
			const index = orderedIds.indexOf(changelogId)
			if (index === -1) return
			const next = orderedIds[index + offset]
			if (next)
				void navigate({ to: "/edit-changelog/$id", params: { id: next } })
			else if (offset === -1) void navigate({ to: "/edit-changelog" })
		},
		[orderedIds, changelogId, navigate],
	)
	useShortcut("ArrowDown, j", () => stepTo(1), {
		enabled: !!changelogId,
		preventDefault: true,
	})
	useShortcut("ArrowUp, k", () => stepTo(-1), {
		enabled: !!changelogId,
		preventDefault: true,
	})

	// Clear preview version when navigating to a different changelog
	useEffect(() => {
		setPreviewVersion(null)
	}, [changelogId, setPreviewVersion])

	// Source the editor's changelog from the filtered list so switching portals
	// while editing closes the editor if the entry doesn't belong to the new
	// active portal — same UX as the docs view.
	const changelog = useMemo(() => {
		return filteredChangelogs.find((c) => c.id === changelogId)
	}, [filteredChangelogs, changelogId])
	const presenceKey = changelog
		? (`changelog:${changelog.id}:${selectedLanguage.code}` as const)
		: undefined
	useJoinDoc(presenceKey)

	// The live editor lives inside ChangelogEntry; keep a getter so the copilot
	// can read the current body as page context.
	const editorGetterRef = useRef<(() => Editor | null) | null>(null)
	const registerEditorGetter = useCallback((getter: () => Editor | null) => {
		editorGetterRef.current = getter
	}, [])
	const getChangelogPageContext = useCallback(
		() => ({
			title: changelog?.title ?? undefined,
			html: editorGetterRef.current?.()?.getHTML(),
		}),
		[changelog?.title],
	)

	const { selectedTranslation, defaultLanguage } = useChangelogTranslations(
		changelog ??
			({
				id: "",
				workspaceId: workspace.id,
				translations: [],
			} as unknown as Changelog & {
				translations?: readonly ChangelogTranslation[]
			}),
		selectedLanguage,
	)

	const isSelectedTranslationRunning =
		useIsTranslationRunning(selectedTranslation)

	const changelogTranslationInFlight =
		selectedLanguage.code !== defaultLanguage && isSelectedTranslationRunning

	const translationTargets = useMemo(
		() =>
			buildTranslationTargets({
				enabledLanguages: workspace.enabledLanguages ?? [],
				defaultLanguage,
				translations: changelog?.translations,
				hasContent: (row) => !isChangelogTranslationEmpty(row),
			}),
		[workspace.enabledLanguages, defaultLanguage, changelog?.translations],
	)

	const emptyLanguageCodes = useMemo(() => {
		if (
			!changelog ||
			!workspace.enabledLanguages ||
			workspace.enabledLanguages.length <= 1
		) {
			return []
		}
		return getEmptyChangelogLanguages(
			changelog,
			workspace.enabledLanguages,
			workspace.defaultLanguage ?? "en",
		)
	}, [changelog, workspace.enabledLanguages, workspace.defaultLanguage])

	return (
		<div className="h-full">
			<MetaInfo title="Changelog" />
			{/* h-full, not a viewport calc: the app card is already inset and, on a
			    phone, ends above the floating nav bar. */}
			<div className="flex h-full w-full overflow-hidden bg-white dark:bg-gray-900">
				{!changelogId ? (
					<div className="flex h-full w-full flex-col">
						<div className="flex-shrink-0">
							<PageHeader
								title={
									<nav
										aria-label="Breadcrumb"
										className="flex min-w-0 max-w-full cursor-default items-center gap-2 text-sm font-medium"
									>
										{multiPortalActive ? (
											<PortalInstanceSwitcher
												activeId={activePortalInstanceId}
												onChange={handlePortalChange}
											/>
										) : (
											<span
												aria-current="page"
												className="-ml-1 flex-none px-1 py-0.5 text-primary"
											>
												Changelog
											</span>
										)}
									</nav>
								}
								titleClassName="text-sm font-medium"
								clearsNavToggle
								className="h-12 max-h-12 min-h-12 border-b border-gray-200 bg-white py-0 pr-3 sm:pl-[17.5px] dark:border-gray-700/80 dark:bg-gray-900"
								actions={
									<div className="ml-auto flex items-center justify-center gap-2">
										<Tooltip label="Search posts">
											<IconButton
												variant="ghost"
												Icon={IconMagnifierOutline18}
												aria-label="Search posts"
												onClick={() =>
													isSearchOpen ? closeSearch() : setIsSearchOpen(true)
												}
											/>
										</Tooltip>
										<RoleCheck not={["VIEWER"]}>
											<PublishPortalPageButton
												name="changelog"
												isPublished={workspace.changelogPublic || false}
												content={<ChangelogSettings />}
												url={getPortalUrl(
													workspace,
													"/changelog",
													activePortalSlug,
												)}
											/>
											<Tooltip label="Create new changelog">
												<IconButton
													Icon={NewRequestIcon}
													iconClassName="h-[13px] w-[13px] !text-primary -translate-x-[0.5px]"
													variant="outline"
													aria-label="Create new changelog"
													onClick={() => void handleCreateNewChangelog()}
												/>
											</Tooltip>
										</RoleCheck>
									</div>
								}
							/>
							{isSearchOpen && (
								<div className="border-b border-gray-200 px-3 py-1.5 dark:border-gray-700/80">
									<div className="mx-auto w-full max-w-4xl sm:px-4">
										<Input
											name="changelog-search"
											variant="borderless"
											autoFocus
											inputClassName="h-8 rounded-lg bg-gray-100 !indent-0 pl-7 pr-9 text-sm ring-0 dark:bg-gray-800"
											iconClassName="left-2 text-gray-600 dark:text-gray-300"
											placeholder="Search posts..."
											icon={IconMagnifierOutline18}
											value={query}
											onChange={(e) => setQuery(e.target.value)}
											onKeyDown={(e) => {
												if (e.key === "Escape") closeSearch()
											}}
											suffix={
												<IconButton
													variant="ghost"
													size="sm"
													Icon={IconXmarkOutline18}
													aria-label="Close search"
													onClick={closeSearch}
												/>
											}
										/>
									</div>
								</div>
							)}
						</div>
						<div className="no-scrollbar flex-1 overflow-y-auto bg-white dark:bg-gray-900">
							{isSearching ? (
								<div className="mx-auto w-full max-w-4xl px-2 pt-4 sm:px-6">
									<ChangelogSearchResults
										changelogs={filteredChangelogs}
										query={query}
										onItemSelected={closeSearch}
									/>
								</div>
							) : filteredChangelogs.length > 0 ? (
								<ChangelogOverview changelogs={filteredChangelogs} />
							) : (
								<div className="flex h-full w-full items-center justify-center p-6">
									<Empty
										className="max-w-[20rem]"
										title="Changelog"
										text="Keep customers up to date with every release."
										variant="left"
										image={
											<img
												src={emptyChangelogImage}
												width={110}
												height={114}
												alt="Empty changelog"
											/>
										}
										action={
											<RoleCheck not={["VIEWER"]}>
												<div className="flex items-center gap-2">
													<Button
														variant="default"
														Icon={NewRequestIcon}
														onClick={() => void handleCreateNewChangelog()}
													>
														New changelog
													</Button>
													<HowItWorksButton article="90840b98-ee11-4eb0-b7a0-c24c8196c646" />
												</div>
											</RoleCheck>
										}
									/>
								</div>
							)}
						</div>
					</div>
				) : changelog ? (
					<LayoutGroup>
						<div className="flex h-full min-w-0 flex-1 overflow-hidden bg-gray-25 dark:bg-gray-900">
							<div className="flex min-w-0 flex-1 flex-col">
								<div className="flex-shrink-0">
									<PageHeader
										leading={
											<MobileBackButton
												to="/edit-changelog"
												label="Back to changelog"
											/>
										}
										title={
											// Below lg the pane cannot hold the trail next to the date and
											// Publish, and the entry title is the editor's heading below.
											<nav
												aria-label="Breadcrumb"
												className="flex min-w-0 max-w-full cursor-default items-center gap-2 text-sm font-medium max-lg:hidden"
											>
												{multiPortalActive ? (
													<PortalInstanceSwitcher
														activeId={activePortalInstanceId}
														onChange={handlePortalChange}
													/>
												) : (
													<button
														type="button"
														onClick={() =>
															void navigate({ to: "/edit-changelog" })
														}
														className="-ml-1 -mr-1 min-w-0 truncate rounded px-1 py-0.5 text-secondary hover:bg-gray-100 dark:hover:bg-gray-800"
													>
														Changelog
													</button>
												)}
												<BreadcrumbSlash />
												<span className="min-w-0 truncate text-primary">
													{getChangelogBreadcrumbTitle({
														translationTitle: selectedTranslation.title,
														changelogTitle: changelog.title,
													})}
												</span>
											</nav>
										}
										titleClassName="text-sm font-medium"
										clearsNavToggle
										className="h-12 max-h-12 min-h-12 border-b border-gray-200 bg-white py-0 pl-2 pr-3 sm:pl-[17.5px] dark:border-gray-700/80 dark:bg-gray-900"
										actions={
											<div className="flex shrink-0 flex-row">
												{/* shrink-0 and no wrapping: the breadcrumb gives way instead,
												    so Publish and the AI toggle survive a phone width. */}
												<div className="ml-auto flex items-center justify-center gap-2">
													{presenceKey && <DocViewers docKey={presenceKey} />}
													<RoleCheck not={["VIEWER"]}>
														<TranslateWithAIButton
															resource="changelog"
															resourceId={changelog.id}
															targets={translationTargets}
														/>
													</RoleCheck>
													{workspace.enabledLanguages &&
														workspace.enabledLanguages.length > 1 && (
															<LanguageSelector
																languages={[
																	...new Set([
																		workspace.defaultLanguage ?? "en",
																		...workspace.enabledLanguages,
																	]),
																]}
																selectedLanguage={selectedLanguage}
																onLanguageSelect={setSelectedLanguage}
																emptyLanguageCodes={emptyLanguageCodes}
																variant="code-only"
															/>
														)}
													<ChangelogActions
														changelog={changelog}
														emptyLanguageCodes={emptyLanguageCodes}
													/>

													<div className="ml-1 flex items-center gap-1">
														{/* Settings live on the changelog list; an open
														    entry just links out. */}
														<RoleCheck not={["VIEWER"]}>
															{changelog.published && (
																<Tooltip label="Open changelog">
																	<IconButton
																		Icon={ExternalLink}
																		variant="ghost"
																		onClick={() =>
																			window.open(
																				getPortalUrl(
																					workspace,
																					`/changelog/${getChangelogSlug(changelog, workspace.timezone)}`,
																					activePortalSlug,
																				),
																				"_blank",
																			)
																		}
																	/>
																</Tooltip>
															)}
														</RoleCheck>
														{/* Extra breathing room between the buttons and the icons. */}
														<CopilotToggleButton
															scope={{
																kind: "changelog",
																changelogId: changelog.id,
															}}
														/>
														<ChangelogOverflowMenu changelog={changelog} />
													</div>
												</div>
											</div>
										}
									/>
								</div>

								{previewVersion && (
									<RestorePreviewBanner
										changelogId={changelogId}
										version={previewVersion}
										defaultLanguage={defaultLanguage}
									/>
								)}

								<div
									className={cn(
										"no-scrollbar relative flex-1 bg-white pb-40 dark:bg-gray-900",
										changelogTranslationInFlight
											? "overflow-hidden"
											: "overflow-y-auto",
									)}
								>
									{changelogTranslationInFlight && (
										<TranslationOverlay languageName={selectedLanguage.name} />
									)}
									<div className="flex h-full w-full justify-center p-6">
										<div className="w-full max-w-4xl">
											{previewVersion ? (
												<ChangelogPreview
													version={previewVersion}
													key={`preview-${previewVersion.id}`}
													blurhash={coverBlurhashFor(previewVersion.imageUrl, [
														changelog,
														...(changelog?.translations ?? []),
													])}
												/>
											) : (
												<ChangelogEntry
													changelog={changelog}
													registerEditorGetter={registerEditorGetter}
													key={changelogId}
												/>
											)}
										</div>
									</div>
								</div>
							</div>

							<CopilotOverlay
								layout="push"
								scope={{ kind: "changelog", changelogId: changelog.id }}
								getPageContext={getChangelogPageContext}
								contextTitle={changelog.title ?? undefined}
							/>
						</div>
					</LayoutGroup>
				) : null}
			</div>
		</div>
	)
}
